/**
 * PaymentConfirmationPage - Post-Stripe checkout confirmation
 * MODULE: core/auth/frontend/pages/PaymentConfirmationPage.tsx
 * RECENT: 2026-06-13 [2.2.1383] — Signup users continue to tenant-select after verified checkout session.
 */
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, Button, Spinner, Alert } from 'flowbite-react';
import { getApiBaseUrl } from '@shared/lib/frontend/api/config';
import {
  loadSignupPendingState,
  markSignupPaymentComplete,
} from '../utils/persistSignupSession';
import { HiCheck, HiX } from 'react-icons/hi';

const API_BASE = `${getApiBaseUrl()}/api/v1/auth/subscription`;

type Status = 'loading' | 'success' | 'error';

interface SessionData {
  success: boolean;
  email?: string;
  plan_tier?: string;
  subscription_id?: string;
  payment_status?: string;
}

export function PaymentConfirmationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState<Status>('loading');
  const [data, setData] = useState<SessionData | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setErrorMsg('No session ID');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/session?session_id=${encodeURIComponent(sessionId)}`);
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setStatus('error');
          setErrorMsg(json.detail || 'Could not verify payment');
          return;
        }
        setData(json);
        setStatus('success');
      } catch {
        if (!cancelled) {
          setStatus('error');
          setErrorMsg('Network error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  const planName = data?.plan_tier ? data.plan_tier.charAt(0).toUpperCase() + data.plan_tier.slice(1) : '';

  const handleContinue = () => {
    const signupPending = loadSignupPendingState();
    if (signupPending?.userId && sessionId) {
      markSignupPaymentComplete(sessionId);
      navigate('/tenant-select', {
        replace: true,
        state: {
          userId: signupPending.userId,
          tenantResolution: signupPending.tenantResolution,
          email: signupPending.email,
        },
      });
      return;
    }
    navigate(`/account-setup?session_id=${sessionId || ''}`, { replace: true });
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <Card className="max-w-md w-full mx-auto p-8 text-center">
          <Spinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-700 dark:text-gray-300 font-medium">Processing your payment...</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Please don&apos;t close this window.</p>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <Card className="max-w-md w-full mx-auto p-6">
          <div className="flex justify-center text-red-500 mb-4">
            <HiX className="w-16 h-16" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center">Payment could not be processed</h2>
          {errorMsg && <Alert color="failure" className="mt-4">{errorMsg}</Alert>}
          <div className="mt-6 flex flex-col gap-2">
            <Link to="/subscription-setup">
              <Button color="blue" className="w-full">Try again</Button>
            </Link>
            <a href="mailto:support@brazn.io" className="text-sm text-blue-600 hover:underline text-center">
              Contact support
            </a>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <Card className="max-w-md w-full mx-auto p-6 text-center">
        <div className="flex justify-center text-green-500 mb-4">
          <HiCheck className="w-16 h-16" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Payment successful!</h2>
        {planName && <p className="text-gray-600 dark:text-gray-400 mt-1">You&apos;re now subscribed to {planName}.</p>}
        <Button color="blue" className="w-full mt-6" onClick={handleContinue}>
          Continue setup
        </Button>
      </Card>
    </div>
  );
}
